import React, { useState } from "react";
import { Form, Button, Row, Col, Upload, Image, theme } from "antd";
import {
  CloudUploadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileImageOutlined,
} from "@ant-design/icons";

interface PracticeDataRequestInvoiceTemplatesProps {
  form: any;
  isEditing: boolean;
  getAdminAssetPreviewUrl: (val: any, assetKey: string) => string;
  onAssetUpload: (field: any, file: File) => void;
}

const PracticeDataRequestInvoiceTemplates: React.FC<
  PracticeDataRequestInvoiceTemplatesProps
> = ({ form, isEditing, getAdminAssetPreviewUrl, onAssetUpload }) => {
  const { token } = theme.useToken();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  return (
    <Row gutter={[16, 16]}>
      {/* 1. Invoice Header Banner Card */}
      <Col xs={24} md={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Invoice Header Banner
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Header banner image placed atop generated invoice PDFs.
              </div>
            </div>
          </div>

          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.invoiceSampleHeaderUrl !== curr.invoiceSampleHeaderUrl
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const headerVal = getFieldValue("invoiceSampleHeaderUrl");
              const previewUrl = headerVal
                ? getAdminAssetPreviewUrl(headerVal, "header")
                : null;

              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px 16px",
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    border: `1px dashed ${token.colorBorder}`,
                    minHeight: 160,
                  }}
                >
                  {previewUrl ? (
                    <div style={{ textAlign: "center", width: "100%" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginBottom: 12,
                          maxWidth: "100%",
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Header Preview"
                          style={{
                            maxHeight: 110,
                            maxWidth: "100%",
                            objectFit: "contain",
                            borderRadius: token.borderRadiusSM,
                            padding: 4,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewImage(previewUrl)}
                        >
                          Preview
                        </Button>
                        {isEditing && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                              form.setFieldValue("invoiceSampleHeaderUrl", "")
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <FileImageOutlined
                        style={{
                          fontSize: 32,
                          color: token.colorTextQuaternary,
                          marginBottom: 8,
                        }}
                      />
                      <p
                        style={{
                          color: token.colorTextSecondary,
                          fontSize: 13,
                          margin: 0,
                        }}
                      >
                        Default Acting Office header will be used
                      </p>
                    </div>
                  )}

                  {isEditing && (
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        onAssetUpload("invoiceSampleHeaderUrl", file);
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        style={{ marginTop: 12 }}
                      >
                        {previewUrl ? "Replace Header" : "Upload Header"}
                      </Button>
                    </Upload>
                  )}
                </div>
              );
            }}
          </Form.Item>
        </div>
      </Col>

      {/* 2. Invoice Footer Banner Card */}
      <Col xs={24} md={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Invoice Footer / Signature
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Footer banner or signature graphic placed on invoice PDFs.
              </div>
            </div>
          </div>

          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.invoiceSampleFooterUrl !== curr.invoiceSampleFooterUrl
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const footerVal = getFieldValue("invoiceSampleFooterUrl");
              const previewUrl = footerVal
                ? getAdminAssetPreviewUrl(footerVal, "footer")
                : null;

              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px 16px",
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    border: `1px dashed ${token.colorBorder}`,
                    minHeight: 160,
                  }}
                >
                  {previewUrl ? (
                    <div style={{ textAlign: "center", width: "100%" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginBottom: 12,
                          maxWidth: "100%",
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Footer Preview"
                          style={{
                            maxHeight: 110,
                            maxWidth: "100%",
                            objectFit: "contain",
                            borderRadius: token.borderRadiusSM,
                            padding: 4,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewImage(previewUrl)}
                        >
                          Preview
                        </Button>
                        {isEditing && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                              form.setFieldValue("invoiceSampleFooterUrl", "")
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <FileImageOutlined
                        style={{
                          fontSize: 32,
                          color: token.colorTextQuaternary,
                          marginBottom: 8,
                        }}
                      />
                      <p
                        style={{
                          color: token.colorTextSecondary,
                          fontSize: 13,
                          margin: 0,
                        }}
                      >
                        Default Acting Office footer will be used
                      </p>
                    </div>
                  )}

                  {isEditing && (
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        onAssetUpload("invoiceSampleFooterUrl", file);
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        style={{ marginTop: 12 }}
                      >
                        {previewUrl ? "Replace Footer" : "Upload Footer"}
                      </Button>
                    </Upload>
                  )}
                </div>
              );
            }}
          </Form.Item>
        </div>
      </Col>

      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          src={previewImage}
          preview={{
            visible: !!previewImage,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewImage(null);
            },
          }}
        />
      )}
    </Row>
  );
};

export default PracticeDataRequestInvoiceTemplates;
